<template>
	<div v-if="!sent" class="support-chat-ticket scrollbar bg-shade-3 p-4 mb-3">
		<div class="label">
			<label for="ticketTypeInput">What would you like to do?</label>
		</div>
		<CSelect
			id="ticketTypeInput"
			v-model="ticketType"
			:options="ticketOptions"
			label="Ticket Type"
			:disabled="sent"
			class="mb-3"
		/>
		<div class="label">
			<label for="ticketSummaryInput">{{ summaryLabel }}</label>
		</div>
		<CInput
			id="ticketSummaryInput"
			v-model="summary"
			type="textarea"
			:autosize="true"
			:disabled="sent"
			class="mb-3"
		/>
		<div class="label">
			<label for="formIdInput">Affected Form</label>
		</div>
		<CSelect
			id="formIdInput"
			v-model="formId"
			:options="formOptions"
			label="Form"
			:disabled="sent"
			class="mb-4"
		/>
		<div class="flex items-cetner gap-3 justify-end">
			<CButton class="button--secondary" @click="onCancel">Cancel</CButton>
			<CButton class="support-chat-ticket__submit button--primary" @click="onSubmit">Submit</CButton>
		</div>
	</div>
</template>
<script lang="ts">
	import { defineComponent, watch, ref, computed } from 'vue';
	import { globalStore, type GlobalStore } from 'src/stores/global-store';
	import type { LLMToolCall } from '@cognitoforms/types/server-types/core/services/forms/a-i/model/llm-tool-call';
	import type { PropType } from 'vue';
	import type { SelectOption } from 'src/components/form-controls/Select.vue';
	import CSelect from 'src/components/form-controls/Select.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import type { CreateTicketArgs } from '@cognitoforms/api/services/support-chat-service';
	import CInput from 'src/components/form-controls/Input.vue';

	export default defineComponent({
		components: {
			CInput,
			CSelect,
			CButton
		},
		props: {
			toolCall: {
				type: Object as PropType<LLMToolCall>,
				required: true
			},
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			},
			buttonStatus: {
				type: String as PropType<ButtonStatus>,
				default: 'default'
			}
		},
		emits: ['submit', 'close'],
		setup(props, { emit }) {
			const ticketType = ref<string>(null);
			const summary = ref<string>(null);
			const formId = ref<string>(null);

			const ticketOptions: SelectOption[] = [
				{
					label: 'Ask a question',
					value: 'question'
				},
				{
					label: 'Suggest a feature',
					value: 'feature'
				},
				{
					label: 'Report a problem',
					value: 'problem'
				}
			];

			const summaryLabel = computed(() => {
				switch (ticketType.value) {
				case 'question':
					return 'Question Topic';
				case 'feature':
					return 'Feature Summary';
				default:
					return 'Problem Summary';
				}
			});

			const formOptions = computed<SelectOption[]>(() => {
				return Object.keys(globalStore.forms).map(id => {
					const form = globalStore.forms[id];
					return {
						label: form.Name,
						value: form.Id
					};
				}).sort((a, b) => a.label.localeCompare(b.label));
			});

			watch(() => props.toolCall, () => {
				if (!props.toolCall?.Arguments)
					return;

				const argsJson = props.toolCall.Arguments;
				const parsedArgs = JSON.parse(argsJson) as CreateTicketArgs;
				ticketType.value = parsedArgs.ticketType.toLowerCase();
				summary.value = parsedArgs.summary;
				formId.value = parsedArgs.formId;
			}, { immediate: true });

			const sent = ref(false);
			const sendTicket = () => {
				sent.value = true;
				const args: CreateTicketArgs = {
					ticketType: ticketType.value,
					summary: summary.value,
					formId: formId.value
				};
				emit('submit', args);
			};

			const onCancel = () => {
				emit('close');
			};

			const onSubmit = () => {
				if (sent.value)
					return;

				sendTicket();
			};

			return {
				ticketType,
				summary,
				summaryLabel,
				formId,
				ticketOptions,
				formOptions,
				sent,
				onCancel,
				onSubmit
			};
		}
	});
</script>

<style lang="scss">
	.support-chat-ticket {
		max-height: 75%;

		textarea {
			max-height: 108px;
			resize: none;
		}
	}

</style>
